<script setup lang="ts">
import { useGetCompanySettings, type CompanySettings } from "@/api/companies";
import {
  StatusType,
  useCreateStatus,
  useDeleteStatus,
  useGetStatuses,
  useUpdateStatuses,
  useUpdateStatusOrder,
} from "@/api/statuses";
import CheckboxSetting from "@/components/common/CheckboxSetting/CheckboxSetting.vue";
import CoreButton from "@/components/common/CoreButton/CoreButton.vue";
import CoreSaveConfirm from "@/components/common/CoreSaveConfirm/CoreSaveConfirm.vue";
import ExpandableBox from "@/components/common/ExpandableBox/ExpandableBox.vue";
import SettingsTwoColumn from "@/components/layout/settings/SettingsTwoColumn.vue";
import ExpandableBoxSkeleton from "@/components/skeletons/ExpandableBoxSkeleton.vue";
import { useCompanySettings } from "@/composables/company";
import { useDevices } from "@/composables/devices";
import useAuthStore from "@/stores/auth";
import { CONFIRM } from "@/stores/constants";
import useSaveStatusStore from "@/stores/saveStatus";
import {
  ElCard,
  ElForm,
  ElFormItem,
  ElInput,
  ElTooltip,
  type FormInstance,
  type FormItemInstance,
  type FormRules,
  type InputInstance,
} from "element-plus";
import { computed, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import type { ExpandableBoxListItem } from "@/components/common/ExpandableBox/ExpandableBoxItem.vue";
import { useValidation } from "@/composables/validation";

const { t, locale } = useI18n();

const authStore = useAuthStore();
const saveStatusStore = useSaveStatusStore();
const { validateRange, validateFormItem } = useValidation();

const companyId = computed(() => authStore.authUser?.companyId);

const showAddMessage = ref(false);
const messagesBeingDeleted = ref<string[]>([]);
const inputRef = ref<InputInstance>();

const newMessageFormRef = ref<FormInstance>();
const newMessageRef = ref<FormItemInstance>();
const newMessageForm = reactive({
  newMessage: "",
});

const createMessageUpdateError = ref(false);
const newMessageRules = reactive<FormRules>({
  newMessage: [
    {
      trigger: ["change", "blur"],
      message: () => t("validation.requiredField"),
      required: true,
    },
    {
      validator: (_rule, _value, callback) => {
        if (createMessageUpdateError.value) {
          createMessageUpdateError.value = false;
          return callback(t("validation.duplicateField"));
        }
        callback();
      },
    },
  ],
});

const messageListRef = ref();

const titleFormRef = (messageId: string): FormInstance | undefined =>
  messageListRef.value.boxItemRefs[`item${messageId}`].value.updateTitleFormRef;

const updateMessageRules = reactive<FormRules>({
  title: [
    {
      trigger: "blur",
      message: () => t("validation.requiredField"),
      required: true,
    },
    validateRange(1, 128),
  ],
  id: {
    validator: (rule, value, callback) => {
      validationErrors[value] ? callback(validationErrors[value]) : callback();
      delete validationErrors[value];
    },
  },
});

const getCompanySettings = useGetCompanySettings();
const { updateSetting, settingsSaveStatus } = useCompanySettings();
const settings = computed<Partial<CompanySettings>>(
  () => getCompanySettings.data.value ?? {}
);
const getStatuses = useGetStatuses((data) => {
  messagesBeingDeleted.value.forEach((id) => {
    if (data.map((message: { id: string }) => message.id).includes(id)) {
      const index = messagesBeingDeleted.value.indexOf(id);
      if (index !== -1) {
        messagesBeingDeleted.value.splice(index, 1);
      }
    }
  });
});
const createStatus = useCreateStatus(
  () => {
    showAddMessage.value = false;
    newMessageFormRef.value?.resetFields();
  },
  async (error) => {
    if (
      error.response?.data.errors.some(
        (error) => error.details === "Status already exists."
      )
    ) {
      createMessageUpdateError.value = true;
      try {
        await newMessageFormRef.value?.validate();
      } catch (err) {
        return;
      }
    }
  }
);
const validationErrors = reactive<{ [id: string]: string }>({});
const updateStatuses = useUpdateStatuses(
  (message) => {
    delete validationErrors[String(message.data.id)];
    saveStatusStore.setSaveStatus(
      `expandableBoxItem${message.data.id}`,
      CONFIRM.SUCCESS
    );
  },
  async (error, message) => {
    saveStatusStore.setSaveStatus(
      `expandableBoxItem${message.id}`,
      CONFIRM.SAVED
    );

    if (
      error.response?.data.errors.some(
        (error) => error.details === "Status already exists."
      )
    ) {
      validationErrors[message.id] = t("validation.duplicateMessage");
    }

    if (
      error.response?.data.errors.some(
        (error) =>
          error.details === "The label may not be greater than 128 characters."
      )
    ) {
      validationErrors[message.id] = t(
        "validation.maxCharacters",
        { max: 128 },
        128
      );
    }

    try {
      await titleFormRef(message.id)?.validate();
    } catch (e) {
      return;
    }
  }
);
const updateStatusOrder = useUpdateStatusOrder();
const deleteStatus = useDeleteStatus();

const { devicesAtSelectedGroup } = useDevices();

const requireMessageLoading = computed(
  () =>
    getCompanySettings.isLoading.value && !getCompanySettings.isRefetching.value
);
const signOutMessagesEnabled = computed(() => {
  return (
    devicesAtSelectedGroup.value?.some(
      (device) => device.location.is_employee_in_out_enabled
    ) || settings.value.mobile?.movements
  );
});
const addMessageLoading = computed(
  () => createStatus.isLoading.value || getStatuses.isRefetching.value
);

const messages = computed<ExpandableBoxListItem[]>(() => {
  return (
    getStatuses.data.value
      ?.map(
        (message): ExpandableBoxListItem => ({
          id: message.id,
          numericId: Number(message.id),
          title: message.label,
          order: message.order,
          active: message.enabled,
        })
      )
      .sort((field1, field2) => Number(field1.order) - Number(field2.order))
      .filter(
        ({ id }: { id: string }) => !messagesBeingDeleted.value.includes(id)
      ) ?? []
  );
});

const cancelAddingMessage = () => {
  newMessageFormRef.value?.resetFields();
  showAddMessage.value = false;
};

const addMessage = async () => {
  try {
    await newMessageFormRef.value?.validate();
  } catch (error) {
    return;
  }
  if (!companyId.value) return;

  const messagePayload = {
    enabled: true,
    type: StatusType.company,
    item_id: companyId.value,
    label: newMessageForm.newMessage.trim(),
  };

  createStatus.mutate(messagePayload);
};

const updateMessage = (message: ExpandableBoxListItem) => {
  if (!companyId.value) return;

  saveStatusStore.setSaveStatus(
    `expandableBoxItem${message.id}`,
    CONFIRM.PENDING
  );
  updateStatuses.mutate({
    id: message.id,
    label: message.title,
    enabled: message.active ?? false,
    order: message.order ?? 0,
    is_deleted: null,
    type: StatusType.company,
    item_id: companyId.value,
  });
};

const updateMessageOrder = ({
  list,
  id,
}: {
  list: ExpandableBoxListItem[];
  id: string;
}) => {
  const movedListItem = list.find((listItem) => listItem.id === id);
  if (!movedListItem) {
    return;
  }

  updateStatusOrder.mutate({
    id: movedListItem.id,
    order: String(movedListItem.order ?? 0),
  });
};

const triggerDeleteConfirm = (message: ExpandableBoxListItem) => {
  messagesBeingDeleted.value.push(message.id);
  deleteStatus.mutate(message.id);
};

watch(locale, () => {
  const fieldRefs = { newMessageRef: newMessageRef.value };
  validateFormItem(fieldRefs);
});
</script>

<template>
  <SettingsTwoColumn>
    <template #col-one>
      <div class="heading">
        <h3>{{ t("employeeSettings.signOutMessages.title") }}</h3>
        <p>{{ t("employeeSettings.signOutMessages.description") }}</p>
      </div>
      <CheckboxSetting
        :value="settings.employee_message_required"
        :label="t('employeeSettings.signOutMessages.requireMessage')"
        :loading="requireMessageLoading"
        :disabled="!signOutMessagesEnabled"
        :disabled-message="
          t('employeeSettings.tooltips.enablePocketOrIpadEmployeeInOut')
        "
        @change="updateSetting('employee_message_required', Boolean($event))"
      >
        <template #suffix>
          <CoreSaveConfirm
            :status="settingsSaveStatus.employee_message_required"
          />
        </template>
      </CheckboxSetting>
      <div class="add-message-button-container">
        <el-tooltip
          :content="
            t('employeeSettings.tooltips.enablePocketOrIpadEmployeeInOut')
          "
          :show-after="500"
          placement="top"
          :disabled="signOutMessagesEnabled"
        >
          <div>
            <CoreButton
              plain
              :disabled="!signOutMessagesEnabled"
              type="primary"
              @click="showAddMessage = true"
            >
              {{ t("employeeSettings.signOutMessages.addMessage") }}
            </CoreButton>
          </div>
        </el-tooltip>
      </div>
      <transition name="so-fade-in-linear">
        <el-card
          v-if="showAddMessage"
          class="add-message-card"
        >
          <el-form
            ref="newMessageFormRef"
            inline
            hide-required-asterisk
            label-position="top"
            :model="newMessageForm"
            class="new-message-form"
            :rules="newMessageRules"
          >
            <el-form-item
              ref="newMessageRef"
              prop="newMessage"
              :label="t('employeeSettings.signOutMessages.message')"
              class="new-message-form-item"
            >
              <el-input
                ref="inputRef"
                v-model="newMessageForm.newMessage"
                maxlength="128"
                @vue:mounted="inputRef?.focus()"
              />
            </el-form-item>
            <CoreButton
              type="info"
              plain
              :disabled="addMessageLoading"
              @click="cancelAddingMessage"
            >
              {{ t("employeeSettings.signOutMessages.addMessageCancel") }}
            </CoreButton>
            <CoreButton
              type="primary"
              :loading="addMessageLoading"
              @click="addMessage"
            >
              {{ t("employeeSettings.signOutMessages.addMessage") }}
            </CoreButton>
          </el-form>
        </el-card>
      </transition>
      <ExpandableBoxSkeleton :loading="getStatuses.isLoading.value">
        <el-tooltip
          :content="
            t('employeeSettings.tooltips.enablePocketOrIpadEmployeeInOut')
          "
          :show-after="500"
          placement="top"
          :disabled="signOutMessagesEnabled"
        >
          <ExpandableBox
            v-if="messages.length"
            ref="messageListRef"
            :list="messages"
            :disabled="!signOutMessagesEnabled"
            :disable-toggle="updateStatuses.isLoading.value"
            :disable-ordering="updateStatusOrder.isLoading.value"
            :form-rules="updateMessageRules"
            :character-limit="128"
            @update="updateMessage"
            @update-order="updateMessageOrder"
          >
            <template #default="{ listItem }">
              <CoreButton
                :icon="['fal', 'times-circle']"
                type="primary"
                text
                @click="triggerDeleteConfirm(listItem)"
              >
                {{ t("employeeSettings.signOutMessages.removeMessage") }}
              </CoreButton>
            </template>
          </ExpandableBox>
        </el-tooltip>
      </ExpandableBoxSkeleton>
    </template>
  </SettingsTwoColumn>
</template>

<style lang="css" scoped>
.heading {
  margin-bottom: 24px;
}

.add-message-button-container {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
}

.add-message-card {
  margin-bottom: 20px;
  --so-card-padding: 30px;
}

.new-message-form {
  justify-content: flex-end;
}

.new-message-form-item {
  width: 100%;
  margin-right: 0;
}
</style>
